import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  CircularProgress,
  createTheme,
  Dialog,
  IconButton,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState, type FC } from "react";

import { type Association } from "@entities";
import { OrganizationRoles } from "@shared/Roles";
import { phoneToIsraelSignedPhone } from "@shared/helpers/phone-to-israel-signed-phone";
import { associationRepo, userRepo } from "@repositories";

import { useAlert } from "clients/admin/src/desktop/contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { GeneralAlert } from "../../alert/GeneralAlert";
import {
  CardInfoChips,
  CardInfoField,
  CardInfoGrid,
  CardInfoHeader,
  CardInfoSection,
  CardInfoView,
} from "../card-info-view/CardInfoView";
import { SelectButton } from "../../select-buttons/SelectButton";
import { SelectButtonsContainer } from "../../select-buttons/SelectButtonsContainer";
import { UserCardGeneralInfo } from "./UserCardGeneralInfo";
import { UserVolunteering } from "./UserVolunteering";
import MissionList from "../../../pages/missions/components/MissionList";
import {
  hasUsableVolunteerCoordinates,
  RelevantOpportunitiesForVolunteerList,
} from "../../../pages/tables/opportunities-table/RelevantOpportunitiesForVolunteerList";

import "./UserCardPopup.scss";
import { CommunicationRecordManager } from "../../CommunicationRecordManager/CommunicationRecordManager";
import { useCommunities } from "../../../queries/communities.query";
import PhoneNumberCell from "../../../pages/tables/PhoneNumberCell";

interface UserCardPopupProps {
  userData: Association | undefined;
  setUserData: React.Dispatch<React.SetStateAction<Association | undefined>>;
  isAdd?: boolean;
  setIsAdd?: React.Dispatch<React.SetStateAction<boolean>>;
  handleSave?: () => void;
}

export const UserCardPopup: FC<UserCardPopupProps> = (props) => {
  const { communities: teams } = useCommunities();
  const { userData, setUserData, isAdd, setIsAdd, handleSave } = props;
  const [shownCardPart, setShownCardPart] = useState<
    | "general"
    | "communication"
    | "volunteering"
    | "missions"
    | "relevant-opportunities"
  >("general");
  const [generalViewMode, setGeneralViewMode] = useState<"info" | "edit">(
    isAdd ? "edit" : "info",
  );
  const [userDetails, setUserDetails] =
    useState<Partial<Association | undefined>>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const lastSyncedAssociationIdRef = useRef<string | undefined>(undefined);
  const { setAlertText, setPosition, setSeverity, isSidebarOpen } = useAlert();
  const minDate = dayjs().subtract(16, "year");
  const maxDate = dayjs().subtract(100, "year").toDate();
  const { currOrganization } = useOrganization();

  useEffect(() => {
    if (!userData) {
      lastSyncedAssociationIdRef.current = undefined;
      return;
    }
    // HyperTable / queries refetch on window focus and pass a new object reference
    // for the same row - only hydrate local state when the row identity changes.
    if (lastSyncedAssociationIdRef.current !== userData.id) {
      lastSyncedAssociationIdRef.current = userData.id;
      setUserDetails(userData);
      setGeneralViewMode("info");
    }
  }, [userData]);

  useEffect(() => {
    if (isAdd) setGeneralViewMode("edit");
  }, [isAdd]);

  async function onSave() {
    if (isLoading) return;
    setIsLoading(true);

    if (
      !userDetails?.user?.name ||
      !userDetails?.user?.dateOfBirth ||
      !userDetails?.user?.phoneNumber
    ) {
      setAlertText("יש למלא את כל השדות");
      setIsLoading(false);
      setPosition("top");
      return;
    }

    const formattedPhone = phoneToIsraelSignedPhone(
      userDetails.user.phoneNumber,
    );

    if (isAdd) {
      const phoneRegex = /^\+972\d{9}$/;
      if (!phoneRegex.test(formattedPhone)) {
        setIsLoading(false);
        return setAlertText("מספר הטלפון אינו תקין");
      }
    }

    if (userDetails?.user?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.user.email)) {
      setAlertText("יש להזין כתובת אימייל תקינה");
      setIsLoading(false);
      setPosition("top");
      return;
    }

    // If (userDetails.id && !isValidID(userDetails.user.numberID)) {
    //   setPosition("top");
    //   setAlertText("מספר תעודת זהות אינו תקין");
    //   setIsLoading(false);
    //   return;
    // }

    if (new Date(userDetails?.user.dateOfBirth) > minDate.toDate()) {
      setPosition("top");
      setAlertText("ניתן להשתמש במערכת שלנו מגיל 16 ומעלה");
      setIsLoading(false);
      return;
    }

    if (isNaN(new Date(userDetails?.user.dateOfBirth).getTime())) {
      setPosition("top");
      setAlertText("יש להזין תאריך לידה תקין");
      setIsLoading(false);
      return;
    }

    if (new Date(userDetails?.user.dateOfBirth) < maxDate) {
      setPosition("top");
      setAlertText("יש להזין את תאריך לידה תקין");
      setIsLoading(false);
      return;
    }
    if (isAdd) {
      const isUser = await userRepo.findOne({
        where: { phoneNumber: formattedPhone },
      });
      if (isUser) {
        setPosition("top");
        setAlertText("קיים משתמש במערכת עם מספר טלפון זה");
        setIsLoading(false);
        return;
      }
    }

    const { id, phoneNumber, ...restUserDetails } = userDetails.user || {};

    if (isAdd) {
      // Inserting the new User
      const newUser = await userRepo.insert({
        ...restUserDetails,
        phoneNumber: formattedPhone,
      });

      const association = await associationRepo.insert({
        organizationId: currOrganization?.id,
        comments: userDetails?.comments,
        statusId: userDetails.status?.id ?? "",
        activityStatus: userDetails?.activityStatus ?? "NEW",
        roles: [OrganizationRoles.Volunteer],
        userId: newUser.id,
      });
      if (userDetails.user.skills?.length) {
        await userRepo.relations(newUser).skills.insert(
          userDetails.user.skills.map((skill) => ({
            skillId: skill.skillId,
            userId: newUser.id,
          })),
        );
      }
      if (userDetails.teams?.length) {
        await associationRepo
          .relations(association!)
          .teams.insert(userDetails.teams || []);
      } else {
        await associationRepo
          .relations(association!)
          .teams.insert([{
            teamId: teams?.find((team) => team.isMainOrganizationTeam)?.id || "",
          }]);
      }
    } else if (userDetails.id) {
      const { numberID, email, name, ...restUserDetailsEdit } = restUserDetails || {};
      const updatedUser = await userRepo.update(userDetails.user.id, {
        ...restUserDetailsEdit,
      });

      if (userDetails)
        await associationRepo.update(userDetails.id, {
          comments: userDetails.comments,
          statusId: userDetails.status?.id ?? "",
          scholarshipsHoursNumber: userDetails.scholarshipsHoursNumber,
          activityStatus: userDetails.activityStatus,
          personalDetails: {
            name,
            email,
            numberID,
          }
        });

      if (userDetails.user.skills?.length) {
        await userRepo.relations(updatedUser).skills.deleteMany({
          where: { userId: userDetails.user.id } // Remove all skills first,
        });
        await userRepo.relations(updatedUser).skills.insert(
          userDetails.user.skills.map((skill) => ({
            skillId: skill.skillId,
          })),
        );
      }
      if (!isAdd && userData) await associationRepo.relations(userData).teams.deleteMany({ where: { associationId: userData.id } });
      await associationRepo.relations(userData!).teams.insert(userDetails.teams || []);
    }
    setUserData(undefined);
    setUserDetails({});
    setAlertText(
      isAdd ? "המשתמש/ת נוסף למערכת בהצלחה" : "הפרטים עודכנו בהצלחה",
    );
    setSeverity("success");
    if (handleSave) handleSave();
    if (setIsAdd) setIsAdd(false);
    setIsLoading(false);
  }

  function closePopup() {
    setShownCardPart("general");
    setGeneralViewMode("info");
    setUserDetails({});
    if (setIsAdd) setIsAdd(false);
    setUserData(undefined);
  }

  const theme = createTheme({
    palette: {
      primary: {
        main: "#05204CF2",
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={!!userData || !!isAdd}
        onClose={closePopup}
        className="user-card-popup"
      >
        <div className="user-card-container">
          <IconButton onClick={closePopup} className="close-button">
            <ClearIcon className="close-icon" />
          </IconButton>
          <div className="user-card-content">
            <SelectButtonsContainer>
              <SelectButton
                onClick={() => setShownCardPart("general")}
                content="מידע כללי"
                isChosen={shownCardPart === "general"}
              />
              {!isAdd && (
                <SelectButton
                  onClick={() => setShownCardPart("volunteering")}
                  content="התנדבויות"
                  isChosen={shownCardPart === "volunteering"}
                />
              )}
              {!isAdd && (
                <SelectButton
                  onClick={() => setShownCardPart("relevant-opportunities")}
                  content="משימות רלוונטיות"
                  isChosen={shownCardPart === "relevant-opportunities"}
                />
              )}
              {!isAdd && currOrganization?.organizationType === "PREMIUM" && (
                <SelectButton
                  onClick={() => setShownCardPart("communication")}
                  content="תקשורת"
                  isChosen={shownCardPart === "communication"}
                />
              )}
              {!isAdd && currOrganization?.organizationType === "PREMIUM" && (
                <SelectButton
                  onClick={() => setShownCardPart("missions")}
                  content="מטלות"
                  isChosen={shownCardPart === "missions"}
                />
              )}
            </SelectButtonsContainer>
            {shownCardPart === "general" && (isAdd || generalViewMode === "edit") && (
              <UserCardGeneralInfo
                userDetails={userDetails}
                setUserDetails={setUserDetails}
                isAdd={!!isAdd}
                userId={userDetails?.id || ""}
              />
            )}
            {shownCardPart === "general" && !isAdd && generalViewMode === "info" && (
              <VolunteerInfoView
                userDetails={userDetails}
                onEdit={() => setGeneralViewMode("edit")}
              />
            )}
            {shownCardPart === "volunteering" && (
              <UserVolunteering association={userDetails} />
            )}
            {shownCardPart === "relevant-opportunities" && !isAdd && (
              <RelevantOpportunitiesForVolunteerList
                userId={userDetails?.user?.id ?? ""}
                userName={
                  userDetails?.personalDetails?.name ||
                  userDetails?.user?.name ||
                  ""
                }
                associationId={userDetails?.id ?? ""}
                organizationId={currOrganization?.id ?? ""}
                hasVolunteerCoordinates={hasUsableVolunteerCoordinates(
                  userDetails?.user?.coordinates,
                )}
              />
            )}
            {shownCardPart === "communication" && (
              <CommunicationRecordManager
                associationId={userDetails?.id}
                type="volunteer"
                onSave={() => handleSave?.()}
              />
            )}
            {shownCardPart === "missions" && (
              <MissionList
                filter={userDetails?.id ? { associationId: userDetails?.id } : undefined}
                mission={{
                  associationId: userDetails?.id || "",
                  association: userDetails as Association,
                }}
                missionType="USER"
              />
            )}
            {shownCardPart === "general" && (isAdd || generalViewMode === "edit") && (
              <Stack
                width="100%"
                flexDirection="row"
                justifyContent="center"
                gap={1.5}
                className="save-button-container"
              >
                {!isAdd && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setUserDetails(userData);
                      setGeneralViewMode("info");
                    }}
                    sx={{ borderRadius: 2, textTransform: "none", px: 2 }}
                  >
                    ביטול
                  </Button>
                )}
                <Button className="save-button" onClick={onSave}>
                  {isLoading ? (
                    <CircularProgress sx={{ color: "white" }} />
                  ) : isAdd ? "הוספה" : "שמירה"}
                </Button>
              </Stack>
            )}
          </div>
        </div>
        <GeneralAlert />
      </Dialog>
    </ThemeProvider>
  );
};

function VolunteerInfoView({
  userDetails,
  onEdit,
}: {
  userDetails: Partial<Association | undefined>;
  onEdit: () => void;
}) {
  const user = userDetails?.user;
  const displayName =
    userDetails?.personalDetails?.name || user?.name || "-";
  const displayEmail =
    userDetails?.personalDetails?.email || user?.email || "-";
  const displayIdNumber =
    userDetails?.personalDetails?.numberID || user?.numberID || "-";
  const age = user?.dateOfBirth
    ? dayjs().diff(dayjs(user.dateOfBirth), "year")
    : null;
  const activityStatusLabel =
    userDetails?.activityStatus === "NEW"
      ? "חדש"
      : userDetails?.activityStatus === "ACTIVE"
        ? "פעיל"
        : userDetails?.activityStatus === "LEGACY"
          ? "מאגר ישן"
          : userDetails?.activityStatus === "INACTIVE"
            ? "לא פעיל"
            : "-";
  const genderLabel =
    user?.gender === "MALE"
      ? "זכר"
      : user?.gender === "FEMALE"
        ? "נקבה"
        : user?.gender
          ? "אחר"
          : "-";
  const teamNames =
    userDetails?.teams
      ?.map((team) => team.team?.name)
      .filter((name): name is string => !!name) ?? [];
  const skillNames =
    user?.skills
      ?.map((skill) => skill.skill?.name)
      .filter((name): name is string => !!name) ?? [];
  const preferredPositions = userDetails?.preferredPositions ?? [];

  return (
    <CardInfoView>
      <CardInfoSection>
        <CardInfoHeader
          title={displayName}
          onEdit={onEdit}
          subtitle={
            <Stack spacing={0.75}>
              {user?.phoneNumber ? (
                <PhoneNumberCell phoneNumber={user.phoneNumber} />
              ) : (
                <Typography variant="body2">אין טלפון</Typography>
              )}
              <Typography variant="body2">{displayEmail}</Typography>
            </Stack>
          }
          extra={
            <Box sx={{ mt: 1.25 }}>
              <CardInfoChips
                items={[
                  `פעילות: ${activityStatusLabel}`,
                  `סטטוס: ${userDetails?.status?.title || "ללא סטטוס"}`,
                  user?.isVerified ? "מאומת" : "לא מאומת",
                ]}
              />
            </Box>
          }
        />
      </CardInfoSection>

      <CardInfoSection
        title="הערות פנימיות"
        sx={
          userDetails?.comments
            ? {
                borderColor: "warning.light",
                bgcolor: "rgba(255, 193, 7, 0.08)",
              }
            : undefined
        }
      >
        <Typography
          sx={{
            whiteSpace: "pre-wrap",
            fontSize: "1.125rem",
            fontWeight: 500,
            lineHeight: 1.55,
            color: userDetails?.comments ? "text.primary" : "text.secondary",
          }}
        >
          {userDetails?.comments || "-"}
        </Typography>
      </CardInfoSection>

      <CardInfoSection title="פרטים אישיים">
        <CardInfoGrid>
          <CardInfoField label="גיל">{age != null ? String(age) : "-"}</CardInfoField>
          <CardInfoField label="מגדר">{genderLabel}</CardInfoField>
          <CardInfoField label="ת.ז">{displayIdNumber}</CardInfoField>
          <CardInfoField label="שעות למלגאים">
            {userDetails?.scholarshipsHoursNumber != null
              ? String(userDetails.scholarshipsHoursNumber)
              : "-"}
          </CardInfoField>
          <CardInfoField label="כתובת" fullWidth>
            {user?.address || "-"}
          </CardInfoField>
        </CardInfoGrid>
      </CardInfoSection>

      <CardInfoSection title="שיוך והעדפות">
        <CardInfoGrid>
          <CardInfoField label="פרויקטים" fullWidth>
            <CardInfoChips items={teamNames} />
          </CardInfoField>
          <CardInfoField label="יכולות" fullWidth>
            <CardInfoChips items={skillNames} />
          </CardInfoField>
          {preferredPositions.length > 0 && (
            <CardInfoField label="העדפות" fullWidth>
              <CardInfoChips items={preferredPositions} />
            </CardInfoField>
          )}
        </CardInfoGrid>
      </CardInfoSection>
    </CardInfoView>
  );
}
